import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, FileText, CheckCircle, XCircle, Clock,
  ChevronDown, ChevronUp, AlertTriangle, ExternalLink, X
} from 'lucide-react';
import api from '../services/api';
import ReactMarkdown from 'react-markdown';

const PrivacyCenter = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [consentStatus, setConsentStatus] = useState(null);
  const [consentHistory, setConsentHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [revoking, setRevoking] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [policyModal, setPolicyModal] = useState({ open: false, title: '', content: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        api.get('/lgpd/consent-status'),
        api.get('/lgpd/consent-history')
      ]);
      setConsentStatus(statusRes.data.data.consents);
      setConsentHistory(historyRes.data.data);
    } catch (err) {
      setError('Erro ao carregar dados de privacidade.');
      console.error('Error loading privacy data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (consentType) => {
    setRevoking(consentType);
    setError(null);
    try {
      await api.post('/lgpd/revoke-consent', { consentType });
      setSuccess('Consentimento revogado com sucesso.');
      await loadData();
    } catch (err) {
      const msg = err.response?.data?.message || 'Erro ao revogar consentimento.';
      setError(msg);
    } finally {
      setRevoking(null);
    }
  };

  const openPolicyModal = async () => {
    try {
      const res = await api.get('/lgpd/privacy-policy');
      setPolicyModal({
        open: true,
        title: res.data.data.title || 'Politica de Privacidade',
        content: res.data.data.content || ''
      });
    } catch (err) {
      setError('Erro ao carregar politica de privacidade.');
    }
  };

  const consentLabels = {
    terms_of_use: { label: 'Termos de Uso', icon: FileText, required: true },
    privacy_policy: { label: 'Politica de Privacidade', icon: Shield, required: true },
    health_data_processing: { label: 'Tratamento de Dados de Saude', icon: Shield, required: false },
    ai_data_processing: { label: 'Processamento por IA', icon: Shield, required: false },
    marketing: { label: 'Comunicacoes e Marketing', icon: Shield, required: false }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-brand-100 rounded-lg">
            <Shield className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Central de Privacidade</h1>
            <p className="text-sm text-gray-600">
              Gerencie seus consentimentos e dados pessoais conforme a LGPD
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-center gap-2">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Consent Status Cards */}
      <div className="space-y-3 mb-6">
        {consentStatus && Object.entries(consentStatus).map(([type, status]) => {
          const config = consentLabels[type] || { label: type, icon: Shield, required: false };
          const Icon = config.icon;

          return (
            <div key={type} className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${status.accepted ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <Icon className={`h-5 w-5 ${status.accepted ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium text-gray-900">{config.label}</h3>
                      {config.required && (
                        <span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                          Obrigatorio
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {status.accepted
                        ? `Aceito em ${new Date(status.acceptedAt).toLocaleDateString('pt-BR')} - Versao ${status.version}`
                        : 'Nao aceito'
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {status.accepted ? (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <CheckCircle className="h-4 w-4" />
                      Ativo
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-gray-400 font-medium">
                      <XCircle className="h-4 w-4" />
                      Pendente
                    </span>
                  )}

                  {status.accepted && !config.required && (
                    <button
                      onClick={() => handleRevoke(type)}
                      disabled={revoking === type}
                      className="px-3 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      {revoking === type ? 'Revogando...' : 'Revogar'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard/privacy/correction-request')}
          className="bg-white rounded-lg shadow-sm border p-4 text-left hover:border-brand-300 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FileText className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">Solicitar Correcao de Dados</h3>
              <p className="text-xs text-gray-500">Solicite a correcao de dados pessoais incorretos</p>
            </div>
          </div>
        </button>

        <button
          onClick={openPolicyModal}
          className="bg-white rounded-lg shadow-sm border p-4 text-left hover:border-brand-300 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-100 rounded-lg">
              <ExternalLink className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">Politica de Privacidade</h3>
              <p className="text-xs text-gray-500">Visualizar a politica de privacidade completa</p>
            </div>
          </div>
        </button>
      </div>

      {/* Consent History */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">
              Historico de Consentimentos ({consentHistory.length})
            </span>
          </div>
          {showHistory ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </button>

        {showHistory && (
          <div className="border-t divide-y">
            {consentHistory.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-500 text-center">
                Nenhum registro encontrado.
              </div>
            ) : (
              consentHistory.map(log => (
                <div key={log.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-900">
                      {consentLabels[log.consentType]?.label || log.consentType}
                      <span className="text-gray-500"> v{log.version}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    {log.revokedAt ? (
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                        Revogado em {new Date(log.revokedAt).toLocaleDateString('pt-BR')}
                      </span>
                    ) : log.accepted ? (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                        Aceito
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                        Recusado
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* DPO Contact */}
      <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
        <p className="text-sm text-brand-800">
          <strong>Encarregado de Dados (DPO):</strong> Para duvidas sobre privacidade e
          protecao de dados, entre em contato: <a href="mailto:dpo@abagia.ai" className="underline">dpo@abagia.ai</a>
        </p>
      </div>

      {/* Privacy Policy Modal */}
      {policyModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">{policyModal.title}</h2>
              <button
                onClick={() => setPolicyModal({ open: false, title: '', content: '' })}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto prose prose-sm max-w-none">
              <ReactMarkdown>{policyModal.content}</ReactMarkdown>
            </div>
            <div className="px-6 py-3 border-t flex justify-end">
              <button
                onClick={() => setPolicyModal({ open: false, title: '', content: '' })}
                className="px-4 py-2 bg-brand-700 text-white text-sm rounded-lg hover:bg-brand-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyCenter;
